import { useConnector } from 'react-instantsearch';
import { fromUnixTime, getUnixTime } from 'date-fns';

/**
 * Filtro de disponibilidad (alquiler temporal / MED_EST) "headless".
 *
 * Reproduce el filtro histórico de fechas del listado:
 *   `fechaDisponibleDesde <= inicio`  AND  `fechaDisponibleHasta >= fin`
 * (la vivienda debe estar libre toda la ventana pedida). Antes lo aplicaba
 * `<Configure numericFilters>` desde estado React + `router.replace`, lo que
 * competía con el router de InstantSearch por el query string (doble
 * escritor → los params se perdían). Como conector, su estado vive en el
 * uiState (`medEstDates`) y el `routing` lo serializa a `?desde=&hasta=`,
 * dejando un único dueño de la URL.
 *
 * El uiState guarda unix (lo que consume el filtro); el `stateMapping`
 * formatea a `dd-MM-yyyy` para la URL. La API pública trabaja en `Date`.
 */

const $$type = 'nestar.medEstDates';

const isNum = (v: unknown): v is number =>
  v !== '' && v !== null && v !== undefined && Number.isFinite(Number(v));

export type MedEstDatesRenderState = {
  start?: Date;
  end?: Date;
  refine: (start: Date, end: Date) => void;
  clear: () => void;
};

type WidgetParams = { startAttribute?: string; endAttribute?: string };

const connectMedEstDates =
  (
    renderFn: (state: any, isFirstRender: boolean) => void,
    unmountFn: () => void = () => {},
  ) =>
  (widgetParams: WidgetParams) => {
    const {
      startAttribute = 'fechaDisponibleDesde',
      endAttribute = 'fechaDisponibleHasta',
    } = widgetParams || {};

    const makeRefine = (helper: any) => (start: Date, end: Date) => {
      const state = helper.state
        .removeNumericRefinement(startAttribute)
        .removeNumericRefinement(endAttribute)
        .addNumericRefinement(startAttribute, '<=', getUnixTime(start))
        .addNumericRefinement(endAttribute, '>=', getUnixTime(end));
      helper.setState(state.resetPage()).search();
    };

    const makeClear = (helper: any) => () => {
      const state = helper.state
        .removeNumericRefinement(startAttribute)
        .removeNumericRefinement(endAttribute);
      helper.setState(state.resetPage()).search();
    };

    return {
      $$type,
      init(initOptions: any) {
        renderFn(
          {
            ...this.getWidgetRenderState(initOptions),
            instantSearchInstance: initOptions.instantSearchInstance,
          },
          true,
        );
      },
      render(renderOptions: any) {
        renderFn(
          {
            ...this.getWidgetRenderState(renderOptions),
            instantSearchInstance: renderOptions.instantSearchInstance,
          },
          false,
        );
      },
      getRenderState(renderState: any, renderOptions: any) {
        return {
          ...renderState,
          medEstDates: this.getWidgetRenderState(renderOptions),
        };
      },
      getWidgetRenderState({ helper }: any) {
        const [start] = helper.getNumericRefinement(startAttribute, '<=') || [];
        const [end] = helper.getNumericRefinement(endAttribute, '>=') || [];
        return {
          start: isNum(start) ? fromUnixTime(start) : undefined,
          end: isNum(end) ? fromUnixTime(end) : undefined,
          refine: makeRefine(helper),
          clear: makeClear(helper),
          widgetParams,
        };
      },
      dispose({ state }: any) {
        unmountFn();
        return state
          .removeNumericRefinement(startAttribute)
          .removeNumericRefinement(endAttribute);
      },
      getWidgetUiState(uiState: any, { searchParameters }: any) {
        const [start] =
          searchParameters.getNumericRefinements(startAttribute)['<='] || [];
        const [end] =
          searchParameters.getNumericRefinements(endAttribute)['>='] || [];
        if (!isNum(start) || !isNum(end)) return uiState;
        return { ...uiState, medEstDates: { start, end } };
      },
      getWidgetSearchParameters(searchParameters: any, { uiState }: any) {
        let sp = searchParameters.setQueryParameters({
          numericRefinements: {
            ...searchParameters.numericRefinements,
            [startAttribute]: {},
            [endAttribute]: {},
          },
        });
        const dates = uiState.medEstDates;
        if (!dates || !isNum(dates.start) || !isNum(dates.end)) return sp;
        sp = sp.addNumericRefinement(startAttribute, '<=', dates.start);
        sp = sp.addNumericRefinement(endAttribute, '>=', dates.end);
        return sp;
      },
    };
  };

export const useMedEstDates = (
  props: WidgetParams = {},
): MedEstDatesRenderState =>
  useConnector(connectMedEstDates as any, props) as MedEstDatesRenderState;
